//================================================================
//  Component: Navbar
//================================================================

//  Purpose: The Navbar for Events App

//  Example:
//    <Navbar></Navbar>


//================================================================


//Libraries
import React, { useState, useEffect, useContext, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

// Contexts
import { GetAppSize, GetUser } from '../../Library/GlobalContexts.js';

//Components
import Logout from '../../Library/Logout';
import NavItem from './NavItem';

//Images
import LLLogo from '../Images/Icon_LLLogo.png';
import HelpDefault from '../Images/Icon_Help_Default.svg';
import HelpActive from '../Images/Icon_Help_Active.svg';
import LLLogoMobile from '../Images/Icon_LLLogo_Green.svg';
import HamburgerMenu from '../Images/Icon_MobileMenu_White.svg';
import CloseHamburgerMenu from '../Images/Icon_CloseMenu_White.svg';

//CSS
import './Navbar.css';


export default function Navbar() {

  //------------------------------------------------------
  //  useContexts and React Router
  //------------------------------------------------------

  const getUser = useContext(GetUser);
  const getAppSize = useContext(GetAppSize);
  const routerUseLocation = useLocation();
  const navigate = useNavigate();

  //------------------------------------------------------
  //  Define useStates
  //------------------------------------------------------

  // Used to change between > 'pending', 'success'
  const [pageStatus, setPageStatus] = useState('pending');

  // If the menu is open or closed
  const [menuOpen, setMenuOpen] = useState(false);

  // Is the help icon hovered
  const [isHovered, setIsHovered] = useState(false);

  // Determines if the hamburger menu is open/closed
  const [hamburgerMenuOpen, setHamburgerMenuOpen] = useState(false);

  // Is the help icon active
  const isActive = routerUseLocation.pathname === '/help';

  //------------------------------------------------------
  //  useRef
  //------------------------------------------------------

  const menuRef = useRef(null);

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  //Returns the style of each component
  function changeStyleHandler(link, textColour) {

    if (textColour === undefined) {

      textColour = 'text-black'

    }

    // Exception for the default route '/' > We want to the home icon to be selected
    if (routerUseLocation.pathname === '/' && link.toLowerCase() === '/home') {

      // Update the style to clicked
      return `cursor-pointer font-medium border-b-solid border-b-white border-b-2 mx-[10px] py-[5px] no-underline ${textColour}`;

      // Clicked item
    } else if (link.toLowerCase() === (routerUseLocation.pathname).toLowerCase()) {

      // Update the style to clicked
      return `cursor-pointer font-medium border-b-solid border-b-white border-b-2 mx-[10px] py-[5px] no-underline ${textColour}`;

      // Update the style to NOT clicked
    } else {

      return `cursor-pointer p-[10px] no-underline  ${textColour}`;

    }

  }

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

  // OnLoad
  useEffect(() => {

    if (getUser?.roles !== undefined) {
      setPageStatus('success')
    }

  }, [pageStatus, getUser])


  // Close menu on outside click
  useEffect(() => {

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    
  }, []);

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  return (
    <div className='flex flex-row items-center justify-between bg-[#141C25] text-[white] px-[25px] py-0 h-[70px]'>

      {/* ---------------------------------------------------- */}
      {/*  Nav Header - Desktop                                */}
      {/* ---------------------------------------------------- */}

      <div className='flex flex-row items-center justify-between p-0'>

        {/* ------------------------- */}
        {/*  Logo                     */}
        {/* ------------------------- */}

        {
          getAppSize.width > 768 &&

          <div className='flex items-center'>

            <NavLink to='/'>
              <img className='mr-[20px] cursor-pointer w-[125px] h-6' src={LLLogo} alt='Lendlease Logo' height='64px'></img>
            </NavLink>

          </div>
        }

        {/* ------------------------- */}
        {/*  Links                    */}
        {/* ------------------------- */}

        <div>

          {
            getAppSize.width > 768 &&

            <div className='flex flex-row items-center'>

              {/* Digital Services */}
              <NavItem style={changeStyleHandler('/', 'text-white')} link={'/'} title={'Demand Management'}></NavItem>

              {/* Digital Commercial Request */}
              <NavItem style={changeStyleHandler('/digitalcommercialrequest', 'text-white')} link={'/digitalcommercialrequest'} title={'Digital Commercial Request'} roles={['itUser']}></NavItem>

            </div>
          }

          </div>

      </div>

      {/* ------------------------- */}
      {/*  Nav Menu                 */}
      {/* ------------------------- */}

      {/* Show menu (Small Device) or signout button (Large Device) */}
      {
        getAppSize.width <= 768 ? 

        // MOBILE
        (
          <div>

            {/* Hamburger Menu */}
            <img className='absolute left-[20px] top-[20px] cursor-pointer' src={hamburgerMenuOpen ? CloseHamburgerMenu : HamburgerMenu} alt='menu' width='30px ' height='30px' onClick={() => setHamburgerMenuOpen(!hamburgerMenuOpen)}></img>

            {/* - Dropdown Options */}
            <div className='absolute top-[70px] left-[0px] flex flex-col bg-[#141C25] w-full shadow-[inset_0_5px_6px_-3px_rgba(0,0,0,0.4)]' hidden={!hamburgerMenuOpen}>

              <div className='w-full px-3 py-4 border-b border-white border-b-1' onClick={() => {
                navigate('/')
                setHamburgerMenuOpen(false);
              }}>
                Demand Management
              </div>

              <div className='w-full px-3 py-4 border-b border-white border-b-1' onClick={() => {
                navigate('/digitalcommercialrequest')
                setHamburgerMenuOpen(false);
              }}>
                Digital Commercial Request
              </div>

            </div>

            {/* Logo */}
            <NavLink to='/'>
              <img className='absolute left-[70px] top-[16px] cursor-pointer' src={LLLogoMobile} alt='Lendlease Logo' height='64px'></img>
            </NavLink>

            {/* Help / Support */}
            <div onClick={()=>navigate('/help')} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
              <img className='absolute top-[16px] right-[60px] cursor-pointer' src={isHovered || isActive ? HelpActive : HelpDefault} alt='Lendlease Logo' height='64px'></img>
            </div>

            {/* Avatar / Profile icon (Right) */}
            <label className='absolute top-[16px] right-[15px] cursor-pointer bg-[#8ECDCA] text-[#141c25ec] p-[7px] rounded-[50%] text-md w-[35px] h-[35px] flex justify-center items-center' onClick={() => setMenuOpen(!menuOpen)}>
              {getUser?.givenname[0]}{getUser?.surname[0]}
            </label>  

            {/* ------------------------- */}
            {/*  Dropdown Menus           */}
            {/* ------------------------- */}

            {/* Profile Dropdown (Right) */}
            <div onClick={() => setMenuOpen(!menuOpen)} hidden={!menuOpen} className='flex flex-column z-10 absolute top-[69px] right-[0px] bg-[#141c25ec] w-[175px] px-2 py-1 rounded-sm animate-menuDrop'>

              {/* Account */}
              <NavItem style={changeStyleHandler('/', 'text-white')} link={'/account'} title={'Account'}></NavItem>
      
              {/* Sign out */}
              <Logout></Logout>
              
            </div>

          </div>
        )
        : 
        // DESKTOP
        (
          <div className='flex w-[210px] justify-end items-center' ref={menuRef}>

            {/* Help / Support */}
            <div onClick={()=>navigate('/help')} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
                <img className='mr-[20px] cursor-pointer' src={isHovered || isActive ? HelpActive : HelpDefault} alt='Lendlease Logo' height='64px'></img>
            </div>

            {/* Avatar / Profile Icon (Right) */}
            <label className='cursor-pointer bg-[#8ECDCA] text-[#141c25ec] p-[7px] rounded-[50%] text-md w-[35px] h-[35px] flex justify-center items-center' onClick={() => setMenuOpen(!menuOpen)}>
              {getUser.givenname[0]}{getUser.surname[0]}
            </label>

            {/* Profile Dropdown (Right) */}
            <div hidden={!menuOpen} className='flex flex-column gap-0 p-0 absolute top-[70px] right-[0px] bg-[#141c25ec] w-[150px] rounded-b z-20 shadow-lg'>
                
                <NavLink to='/requests'>
                  <label className='mr-[10px] text-white cursor-pointer py-[10px] px-[12px]' onClick={() => setMenuOpen(false)}>
                    My Account
                  </label>
                </NavLink>

                <hr className='m-0 p-0'></hr>
                
                <Logout></Logout>
              
            </div> 

          </div>

        )

      }
    </div>
  )

}
